import React, { Dispatch, SetStateAction, useState } from "react"
import * as graphql from "lib/types/generated/graphql-types"
import {
  ActionWrapper,
  ButtonWrapper,
  Container,
  LabelWrapper,
  Provider,
  Providers,
  ProviderWrapper,
  StyledActionDropdownButton,
  Title
} from "./FailedShippingFeeModifications.styled"
import ActionInfoBox from "../../../../OperationsDashboard/OperationActions/ActionInfoBox"
import { useMutation, useQuery } from "@apollo/client"
import SecondaryButton from "../../../../Ui/Buttons/SecondaryButton"
import CopyButton from "../../../../Ui/CopyButton"
import { ReactComponent as PaymentIcon } from "../../../../../images/icons/credit-card.svg"
import ErrorMessages from "../../ErrorMessages"
import { handleErrorMessages } from "helpers/errors"
import RESTART_SHIPPING_FEE_MODIFICATION from "../../../../../graphql/mutations/order/modification/shippingFees/RestartModification"
import GET_SHIPPING_FEE_MODIFICATION from "../../../../../graphql/queries/order/GetShippingFeeModification"

type Providers = {
  payment: boolean
}

type Props = {
  failedModification: graphql.OrderModification
  currencyCode: string
  setShowStatusPopup: Dispatch<SetStateAction<boolean>>
  setStartedModification: Dispatch<SetStateAction<string>>
  orderHistory: graphql.OrderHistoryLogEntry[]
  refetch: () => void
}

enum FAILURE {
  PAYMENT = "MODIFY_SHIPPING_FEES_PAYMENT_FAILURE"
}

const FailedShippingFeeModifications = ({
  failedModification,
  setShowStatusPopup,
  setStartedModification,
  orderHistory,
  refetch
}: Props) => {
  const [failedProviders, setFailedProviders] = useState<Providers | undefined>()
  const [paymentAction, setPaymentAction] = useState<graphql.InputActionPaymentDelivery>({
    actionType: graphql.InputActionType.Auto
  })

  const [restartModification, { loading }] = useMutation(RESTART_SHIPPING_FEE_MODIFICATION, {
    onCompleted: () => {
      setShowStatusPopup(true)
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const restartOrderModification = () => {
    setStartedModification(failedModification.id)
    restartModification({
      variables: {
        modificationId: failedModification.id,
        input: {
          ...(failedProviders?.payment && { payment: paymentAction })
        }
      }
    })
  }

  useQuery(GET_SHIPPING_FEE_MODIFICATION, {
    variables: { modificationId: failedModification.id },
    onCompleted: (data) => {
      const modificationDetails = data?.Order?.orderModificationShippingFees
      setFailedProviders({
        payment: modificationDetails?.paymentProvider?.status?.current === FAILURE.PAYMENT
      })
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1)
  const getLabel = (value: string) => capitalize(value.toLowerCase())

  return (
    <>
      <Container>
        <Title>
          <div>
            <h2>Failed Modification</h2>
          </div>
        </Title>
        id: {failedModification.id} <CopyButton string={failedModification.id} />
        <ProviderWrapper>
          <h4>Failed providers:</h4>
          {failedProviders?.payment && <Provider>Payment</Provider>}
        </ProviderWrapper>
        <>
          <ActionInfoBox showManual={false} />
          <Providers>
            {failedProviders?.payment && (
              <div>
                <ActionWrapper>
                  <LabelWrapper>
                    <PaymentIcon />
                    <b>Payment:</b>
                  </LabelWrapper>
                  <StyledActionDropdownButton title={getLabel(paymentAction.actionType)}>
                    <li
                      onClick={() => setPaymentAction({ actionType: graphql.InputActionType.Auto })}
                    >
                      Auto
                    </li>
                    <li
                      onClick={() => setPaymentAction({ actionType: graphql.InputActionType.Skip })}
                    >
                      Skip
                    </li>
                  </StyledActionDropdownButton>
                </ActionWrapper>
              </div>
            )}
          </Providers>
        </>
        <ErrorMessages
          orderHistory={orderHistory}
          operationId={failedModification.id}
          operation={"MODIFICATION"}
        />
        <ButtonWrapper>
          <SecondaryButton handleClick={restartOrderModification} loading={loading}>
            Restart modification
          </SecondaryButton>
        </ButtonWrapper>
      </Container>
    </>
  )
}

export default FailedShippingFeeModifications
